import { ArrowRight, BarChart3, Shield, Star, CreditCard, Clock, Wallet, Award, Smartphone, Lock } from "lucide-react";
import { useSEO } from "@/hooks/use-seo";
import { Button } from "@/components/ui/button";
import { Header } from "@/components/Header";
import { Footer } from "@/components/Footer";
import { CasinoComparison } from "@/components/CasinoComparison";
import mykhailoPhoto from "@assets/image_1773345523383.png";
import rebecaPhoto from "@assets/image_1773345527765.png";

const METRIC_CARDS = [
  { icon: Star,       title: "Celkové hodnocení",     desc: "Naše hodnocení zohledňuje celkový dojem z kasina — design, rychlost, nabídku her, kvalitu podpory i bezpečnost. Každé kasino je ohodnoceno na škále 1–10." },
  { icon: Award,      title: "Bonus a free spiny",    desc: "Porovnáváme výši uvítacího bonusu, počet free spinů a podmínky jejich získání. Zohledňujeme i wagering požadavky, aby byl obraz co nejpřesnější." },
  { icon: Shield,     title: "Licence a bezpečnost",  desc: "Ověřujeme, jakou licencí kasino disponuje (MGA, Curaçao, Anjouan) a jaká bezpečnostní opatření má nastavena — SSL šifrování, KYC procesy, oddělení prostředků hráčů." },
  { icon: Wallet,     title: "Minimální vklad",       desc: "Zobrazujeme nejnižší částku potřebnou k zahájení hry. Nižší minimum je vhodné pro začátečníky, zatímco vyšší vklady často otevírají přístup k lepším bonusům." },
  { icon: Clock,      title: "Doba výběru",           desc: "Porovnáváme, jak rychle kasino zpracuje vaši žádost o výběr. Nejrychlejší kasina vyplácejí do 24 hodin, jiná mohou trvat až 72 hodin." },
  { icon: CreditCard, title: "Platební metody",       desc: "Ukazujeme, jaké platební metody kasino podporuje — od klasických karet přes e-peněženky až po kryptoměny. Široká nabídka je vždy výhodou." },
];

export default function PorovnatPage() {
  useSEO({ title: "Srovnání Kasin 2026 – Porovnejte bonusy a výplaty | HrajBonus", description: "Interaktivní srovnání online kasin. Porovnejte bonusy, výběr her, platební metody a hodnocení zákazníků na jednom místě. Vyberte to nejlepší. 18+",
  url: "https://hrajbonus.com/porovnat", });

  return (
    <div className="min-h-screen bg-[#f5f7fb]">
      <Header />

      <section className="relative pt-16 pb-0 overflow-hidden">
        <div className="absolute inset-0 z-0" style={{ background: "linear-gradient(135deg, #001a4d 0%, #002d7c 50%, #003da5 100%)" }} />
        <div className="absolute inset-0 z-0 opacity-5" style={{ backgroundImage: "radial-gradient(circle at 1px 1px, white 1px, transparent 0)", backgroundSize: "40px 40px" }} />
        <div className="relative z-10 max-w-[1440px] mx-auto px-4 sm:px-6 lg:px-12 xl:px-16 py-14 sm:py-16 text-center">
          <div className="inline-flex items-center gap-2 bg-white/10 border border-white/20 rounded-full px-4 py-1.5 text-white/80 text-xs font-semibold uppercase tracking-widest mb-5">
            <BarChart3 className="w-3.5 h-3.5 text-[#f5c518]" /> Porovnání kasin 2026
          </div>
          <h1 className="text-4xl sm:text-5xl font-black text-white leading-tight mb-4" data-testid="heading-porovnat">
            Porovnání casin: která jsou nejlepší?
          </h1>
          <p className="text-white/75 text-lg leading-relaxed max-w-2xl mx-auto mb-8">
            Vyberte si kasina a porovnejte je vedle sebe. Náš interaktivní nástroj vám ukáže klíčové metriky — od bonusů a free spinů přes licence až po rychlost výběrů — abyste mohli učinit informované rozhodnutí.
          </p>
          <div className="flex flex-wrap items-center justify-center gap-4">
            <a href="#comparison-tool">
              <Button size="default" className="hraj-btn font-bold gap-2" style={{ background: "#ffffff", color: "#003da5" }} data-testid="button-hero-compare">
                Porovnat kasina <ArrowRight className="hraj-arrow w-4 h-4" />
              </Button>
            </a>
            <a href="#jak-funguje">
              <Button size="default" variant="outline" className="hraj-btn border-white/30 text-white font-semibold" style={{ background: "rgba(255,255,255,0.08)" }} data-testid="button-hero-how-it-works">
                Jak to funguje
              </Button>
            </a>
          </div>
        </div>
        <div className="relative z-10">
          <svg viewBox="0 0 1440 44" fill="none" xmlns="http://www.w3.org/2000/svg" className="w-full block" preserveAspectRatio="none" style={{ display: "block", verticalAlign: "bottom", height: "44px" }}>
            <rect x="0" y="40" width="1440" height="4" fill="#f5f7fb" />
            <path d="M0 44L1440 44L1440 0C1200 35 720 40 0 0L0 44Z" fill="#f5f7fb" />
          </svg>
        </div>
      </section>

      <div className="max-w-[1440px] mx-auto px-4 sm:px-6 lg:px-12 xl:px-16 py-10 space-y-10">

        <section id="comparison-tool">
          <div className="text-center mb-8">
            <h2 className="text-3xl font-black text-gray-900 mb-2" data-testid="heading-comparison-tool">Interaktivní porovnání kasin</h2>
            <p className="text-gray-500 text-base">Vyberte 2–5 kasin a porovnejte je vedle sebe podle klíčových parametrů</p>
          </div>
          <CasinoComparison />
        </section>

        <section id="jak-funguje">
          <div className="bg-white rounded-2xl border border-[#dde4ee] p-6 shadow-sm">
            <h2 className="text-2xl font-black text-gray-900 mb-4" data-testid="heading-jak-funguje">Jak funguje naše porovnání casin</h2>
            <p className="text-gray-600 leading-relaxed mb-4">
              Náš porovnávací nástroj vám umožňuje rychle a přehledně porovnat až 5 online kasin vedle sebe. Stačí vybrat kasina z nabídky a okamžitě uvidíte přehled klíčových metrik — od bonusů přes licence až po rychlost výběrů.
            </p>
            <p className="text-gray-600 leading-relaxed mb-4">
              Každé kasino v naší databázi prošlo důkladnou kontrolou naší redakce. Hodnotíme desítky parametrů a vybíráme pouze ty nejdůležitější, aby bylo srovnání srozumitelné i pro začátečníky. U každého parametru zvýrazňujeme nejlepší hodnotu, takže na první pohled vidíte, které kasino vyniká.
            </p>
            <div className="bg-[#f3f6fb] rounded-xl p-4">
              <div className="flex items-start gap-3">
                <BarChart3 className="w-5 h-5 text-[#003da5] mt-0.5 flex-shrink-0" />
                <p className="text-gray-700 text-sm leading-relaxed">
                  <span className="font-bold">Tip:</span> Pro nejlepší výsledky doporučujeme porovnávat kasina se podobným zaměřením — například dvě kasina s vysokými bonusy nebo dvě kasina s rychlými výběry. Lépe tak posoudíte, které z nich je ve své kategorii silnější.
                </p>
              </div>
            </div>
          </div>
        </section>

        <section>
          <div className="text-center mb-8">
            <h2 className="text-2xl font-black text-gray-900 mb-2" data-testid="heading-co-porovnavame">Co všechno porovnáváme</h2>
            <p className="text-gray-500 text-base">Klíčové metriky, které rozhodují o kvalitě online kasina</p>
          </div>
          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4">
            {METRIC_CARDS.map(card => (
              <div key={card.title} className="bg-white rounded-2xl border border-[#dde4ee] p-5 shadow-sm hover:shadow-md transition-shadow" data-testid={`metric-card-${card.title.toLowerCase().replace(/\s+/g, "-")}`}>
                <div className="w-10 h-10 rounded-xl bg-[#f3f6fb] flex items-center justify-center mb-3">
                  <card.icon className="w-5 h-5 text-[#003da5]" />
                </div>
                <h3 className="font-bold text-gray-900 text-sm mb-2">{card.title}</h3>
                <p className="text-gray-600 text-sm leading-relaxed">{card.desc}</p>
              </div>
            ))}
          </div>
        </section>

        <section>
          <div className="bg-amber-50 border border-amber-200 rounded-2xl p-6 shadow-sm" data-testid="responsible-gaming">
            <div className="flex items-start gap-3">
              <Shield className="w-6 h-6 text-amber-600 mt-0.5 flex-shrink-0" />
              <div>
                <h2 className="text-xl font-black text-gray-900 mb-3">Zodpovědné hraní</h2>
                <p className="text-gray-700 leading-relaxed mb-3">
                  Hazardní hry jsou určeny výhradně pro osoby starší 18 let. Hraní by mělo být formou zábavy, nikoli způsobem obživy. Pokud máte pocit, že hráčské návyky ovlivňují váš každodenní život, neváhejte vyhledat odbornou pomoc.
                </p>
                <p className="text-gray-700 leading-relaxed mb-3">
                  Stanovte si předem rozpočet a časový limit. Nikdy nehrajte s penězi, které si nemůžete dovolit prohrát. Pokud potřebujete pomoc, navštivte stránky{" "}
                  <a href="https://www.gamcare.org.uk/" target="_blank" rel="noopener noreferrer" className="text-[#003da5] hover:underline font-semibold" data-testid="link-gamcare-porovnat">GamCare</a>{" "}
                  nebo{" "}
                  <a href="https://www.gambleaware.org/" target="_blank" rel="noopener noreferrer" className="text-[#003da5] hover:underline font-semibold" data-testid="link-gambleaware-porovnat">GambleAware</a>.
                </p>
                <div className="flex items-center gap-2 text-sm text-amber-800 font-semibold">
                  <Lock className="w-4 h-4" />
                  18+ | Hrajte zodpovědně | Podmínky se vztahují
                </div>
              </div>
            </div>
          </div>
        </section>

        <section data-testid="author-bios">
          <div className="text-center mb-6">
            <h2 className="text-2xl font-black text-gray-900 mb-2">Naši autoři</h2>
            <p className="text-gray-500 text-base">Obsah této stránky připravili zkušení odborníci na iGaming</p>
          </div>
          <div className="grid sm:grid-cols-2 gap-6">
            <div className="bg-white rounded-2xl border border-[#dde4ee] p-6 shadow-sm" data-testid="author-bio-mykhailo">
              <div className="flex items-center gap-4 mb-4">
                <div className="w-14 h-14 rounded-full overflow-hidden flex-shrink-0 ring-2 ring-[#dde4ee]">
                  <img src={mykhailoPhoto} alt="Mykhailo Romaenko" className="w-full h-full object-cover" />
                </div>
                <div>
                  <div className="font-bold text-gray-900">Mykhailo Romaenko</div>
                  <div className="text-[#003da5] text-sm font-medium">Senior Content Writer</div>
                </div>
              </div>
              <p className="text-gray-600 text-sm leading-relaxed">
                Mykhailo je zkušený autor zaměřený na iGaming, který se dlouhodobě pohybuje v odvětví online hazardu. Má bohatou praxi v psaní článků, které propojují svět hráčů i profesionálů. Jeho specialitou je vytváření hlubokých analýz, přehledných recenzí a trendově orientovaného obsahu.
              </p>
              <p className="text-gray-600 text-sm leading-relaxed mt-3">
                Své texty staví na detailním výzkumu a praktické zkušenosti, díky čemuž pomáhá hráčům lépe se orientovat v prostředí online casin.
              </p>
            </div>
            <div className="bg-white rounded-2xl border border-[#dde4ee] p-6 shadow-sm" data-testid="author-bio-rebeca">
              <div className="flex items-center gap-4 mb-4">
                <div className="w-14 h-14 rounded-full overflow-hidden flex-shrink-0 ring-2 ring-[#dde4ee]">
                  <img src={rebecaPhoto} alt="Rebeca Jimenez" className="w-full h-full object-cover" />
                </div>
                <div>
                  <div className="font-bold text-gray-900">Rebeca Jimenez</div>
                  <div className="text-[#003da5] text-sm font-medium">Editor & Reviewer</div>
                </div>
              </div>
              <p className="text-gray-600 text-sm leading-relaxed">
                Rebeca je editorka a recenzentka s mnohaletou zkušeností v oblasti online kasin. Specializuje se na ověřování bonusových podmínek, testování platebních metod a hodnocení uživatelského prostředí herních platforem.
              </p>
              <p className="text-gray-600 text-sm leading-relaxed mt-3">
                Díky její pečlivosti a analytickému přístupu jsou naše recenze vždy aktuální, přesné a orientované na potřeby českých hráčů.
              </p>
            </div>
          </div>
        </section>
      </div>

      <Footer />
    </div>
  );
}
